import React from 'react';
import merge from 'lodash/merge';

import {
    ChartConsumer,
    Context as ChartContextProps
} from '@ihme/common/web/components/ChartProvider';
import { ChartZoomProvider } from '@ihme/common/web/components';
import { MapChart } from '@ihme/common/web/components';
import { DataRecord } from '@ihme/common/types';
import _ from '@ihme/common/locale';

import { renderChartCopyright } from '../../../utility/chart-copyright';
import formatChartValue from '../../../utility/formatting/formatChartValue';

import echartsTheme from '../../../theme/echarts';
import { pngIcon } from '../../../theme/icons';

type Props = ChartContextProps & {
    locationId: number;
};

class YLLChart extends React.Component<Props> {
    renderTitle = () => _('factsheets_yll_chart_title');

    renderSubtitle = ({ filters: { age_group_id, year } }) =>
        _('factsheets_yll_chart_subtitle',
            {
                age_group_id: _(`age_group_${age_group_id}`),
                year,
            }
        );

    renderTooltip = (name: string, value: number, measureId: number, metricId: number) =>
        `{title|${name}}\n{n|${_(`long_metric_${metricId}`) + ': '}}{b|${formatChartValue(value)}}{n|${' ' + _(`tooltip_measure_${measureId}`)}}`;

    getSaveFilename = ({
        filters: { cause_id, measure_id, metric_id, age_group_id, location_id, year }
    }) =>
        'YLL Chart' +
        this.getLocalizedFilter(cause_id, 'cause_', ', ') +
        this.getLocalizedFilter(measure_id, 'measure_', ', ') +
        this.getLocalizedFilter(metric_id, 'long_metric_', ', ') +
        this.getLocalizedFilter(location_id, 'location_', ' in ') +
        this.getLocalizedFilter(age_group_id, 'age_group_', ', ') +
        ', ' +
        year;

    getLocalizedFilter = (
        value: string | number,
        localePrefix: string,
        prefix: string = ''
    ): string =>
        value
            ? prefix +
            value
                .toString()
                .split(',')
                .map(id => _(`${localePrefix}${id}`))
                .join(', ')
            : '';

    render() {
        return (
            <ChartZoomProvider>
                <MapChart
                    resizeChartOnWindowResize={false}
                    theme={merge(echartsTheme,
                        {
                            map: {
                                roam: false,
                                silent: true,
                            },
                        }
                    )}
                    height={600}
                    renderTooltip={this.renderTooltip}
                    renderTitle={this.renderTitle}
                    renderSubtitle={this.renderSubtitle}
                    renderGraphic={renderChartCopyright}
                    saveAsImage={{
                        visible: true,
                        filename: this.getSaveFilename,
                        icon: pngIcon,
                    }}
                    generateLocationParams={(record:DataRecord) => {
                        const { columns, locationId: selectedLocationId } = this.props;
                        const valueIndex = columns.indexOf('value');
                        const locationIndex = columns.indexOf('location_id');
                        const measureIdIndex = columns.indexOf('measure_id');
                        const metricIdIndex = columns.indexOf('metric_id');

                        const locationId = record[locationIndex];

                        if (locationId === selectedLocationId) {
                            const value = record[valueIndex];
                            const name = _(`location_${locationId}`);
                            const measureId = record[measureIdIndex];
                            const metricId = record[metricIdIndex];

                            return {
                                label: {
                                    show: true,
                                    align: 'left',
                                    formatter: this.renderTooltip(name, value, measureId, metricId),
                                    padding: 15,
                                    borderRadius: 4,
                                    borderColor: 'rgba(0,0,0,0.2)',
                                    borderWidth: 0.5,
                                    shadowColor: 'rgba(0,0,0,0.2)',
                                    shadowBlur: 10,
                                    shadowOffsetX: 10,
                                    shadowOffsetY: 10,
                                    lineHeight: 24,
                                    backgroundColor: 'rgba(255,255,255)',
                                    rich: {
                                        title: {
                                            fontFamily: '"Helvetica", sans-serif',
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: '#ED4C3B',
                                        },
                                        b: {
                                            fontFamily: '"Helvetica", sans-serif',
                                            fontWeight: 600,
                                        },
                                        n: {
                                            fontFamily: '"Helvetica", sans-serif',
                                            fontWeight: 400,
                                        }
                                    },
                                },
                                selected: true,
                                tooltip: {
                                    show: true,
                                },
                            };
                        }

                        return {};
                    }}
                />
            </ChartZoomProvider>
        );
    }
}

export default props => (
    <ChartConsumer>
        {consumerProps => <YLLChart {...consumerProps} {...props} />}
    </ChartConsumer>
);
